import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import Console from "./pages/Console";
import Logs from "./pages/Logs";
import Settings from "./pages/Settings";
import Archives from "./pages/Archives";
import Shield7 from "./pages/Shield7";
import BioDigital from "./pages/BioDigital";
import BlackBox from "./pages/BlackBox";
import OperatorWelcome from "./pages/OperatorWelcome";
import Treasury from "./pages/Treasury";
import Obsidian from "./pages/Obsidian";
import ObsidianReport from "./pages/ObsidianReport";
import KnowledgeGraph from "./pages/KnowledgeGraph";
import InferenceQueue from "./pages/InferenceQueue";
import PGRL from "./pages/PGRL";
import Timeline from "./pages/Timeline";
import HowItWorks from "./pages/HowItWorks";
import SetPassword from "./pages/SetPassword";
import Unlock from "./pages/Unlock";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Orion from "./pages/Orion";
import Forum from "./pages/Forum";
import Research from "./pages/Research";
import TruthBlocks from "./pages/TruthBlocks";
import Disputes from "./pages/Disputes";
import ConsoleDeposit from "./pages/ConsoleDeposit";
import SystemicSubstrates from "./pages/SystemicSubstrates";
import ApiDocs from "./pages/ApiDocs";
import Wallet from "./pages/Wallet";
import Wallets from "./pages/Wallets";
import Leaderboard from "./pages/Leaderboard";
import Pricing from "./pages/Pricing";
import Burns from "./pages/Burns";
import Submit from "./pages/Submit";
import VerificationQueue from "./pages/VerificationQueue";
import Reserves from "./pages/Reserves";
import ArchitectAudit from "./pages/ArchitectAudit";
import Truths from "./pages/Truths";
import DemoShare from "./pages/DemoShare";
import GrantsArchive from "./pages/GrantsArchive";
import Sky from "./pages/Sky";
import Onboarding from "./pages/Onboarding";
import Origin from "./pages/Origin";
import Thesis from "./pages/Thesis";
import Incidents from "./pages/Incidents";
import AdvisoryPublic from "./pages/AdvisoryPublic";
import GenesisBlock from "./pages/GenesisBlock";
import ArchitectStatus from "./pages/ArchitectStatus";
import ForReviewers from "./pages/ForReviewers";
import Reasoning from "./pages/Reasoning";
import Panel from "./pages/Panel";
import IVoted from "./pages/IVoted";
import { MessageSirchewyyy, InboxThread } from "./pages/MessageSirchewyyy";
import Inbox from "./pages/Inbox";
import ArchitectMessages from "./pages/ArchitectMessages";
import { Signup, Signin } from "./pages/EmailAuth";
import AppShell from "./components/layout/AppShell";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL hash synchronously during render —
  // MUST run BEFORE ProtectedRoute to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* v19.40.5 · Cosmos page retired · content merged into /truths */}
      <Route path="/cosmos" element={<Navigate to="/truths" replace />} />
      <Route path="/cosmic-clock" element={<Navigate to="/truths" replace />} />
      <Route path="/origin" element={<Origin />} />
      <Route path="/about" element={<Origin />} />
      <Route path="/thesis" element={<Thesis />} />
      <Route path="/thesis/:section" element={<Thesis />} />
      <Route path="/incidents" element={<Incidents />} />
      <Route path="/advisory/:advisoryId" element={<AdvisoryPublic />} />
      <Route path="/genesis-block" element={<GenesisBlock />} />
      <Route path="/genesis" element={<GenesisBlock />} />
      <Route path="/architect-status" element={<ArchitectStatus />} />
      <Route path="/status" element={<ArchitectStatus />} />
      <Route path="/for-reviewers" element={<ForReviewers />} />
      <Route path="/reviewers" element={<ForReviewers />} />
      <Route path="/tour" element={<ForReviewers />} />
      <Route path="/reasoning" element={<Reasoning />} />
      <Route path="/panel" element={<Panel />} />
      <Route path="/dossier" element={<Panel />} />
      <Route path="/reviewer" element={<Panel />} />
      <Route path="/i-voted" element={<IVoted />} />
      <Route path="/vote" element={<IVoted />} />
      <Route path="/covenant" element={<IVoted />} />
      <Route path="/accord" element={<IVoted />} />
      <Route path="/message-sirchewyyy" element={<MessageSirchewyyy />} />
      <Route path="/inbox/:token" element={<InboxThread />} />
      <Route path="/treasury" element={<Treasury />} />
      <Route path="/obsidian" element={<Obsidian />} />
      <Route path="/obsidian/report/:reportId" element={<ObsidianReport />} />
      <Route path="/knowledge-graph" element={<KnowledgeGraph />} />
      <Route path="/inference-queue" element={<InferenceQueue />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/guide" element={<HowItWorks />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signin" element={<Signin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/set-password"
        element={
          <ProtectedRoute allowPasswordFlow>
            <SetPassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/unlock"
        element={
          <ProtectedRoute allowPasswordFlow>
            <Unlock />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/console"
        element={
          <ProtectedRoute>
            <AppShell>
              <Console />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/operate/substrates"
        element={
          <ProtectedRoute>
            <AppShell>
              <SystemicSubstrates />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs"
        element={
          <ProtectedRoute>
            <AppShell>
              <Logs />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/archives"
        element={
          <ProtectedRoute>
            <AppShell>
              <Archives />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shield"
        element={
          <ProtectedRoute>
            <AppShell>
              <Shield7 />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bio-digital"
        element={
          <ProtectedRoute>
            <AppShell>
              <BioDigital />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/blackbox"
        element={
          <ProtectedRoute>
            <AppShell>
              <BlackBox />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pgrl"
        element={
          <ProtectedRoute>
            <AppShell>
              <Timeline />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orion"
        element={
          <ProtectedRoute>
            <Orion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forum"
        element={
          <ProtectedRoute>
            <Forum />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger"
        element={<Navigate to="/truths" replace />}
      />
      <Route
        path="/bounties"
        element={<Navigate to="/truths?bucket=proof" replace />}
      />
      <Route
        path="/symbols"
        element={<Navigate to="/sky#legend" replace />}
      />
      <Route
        path="/architect/audit"
        element={
          <AppShell>
            <ArchitectAudit />
          </AppShell>
        }
      />
      <Route
        path="/truth-blocks"
        element={
          <AppShell>
            <TruthBlocks />
          </AppShell>
        }
      />
      <Route path="/disputes" element={<Disputes />} />
      <Route path="/challenges" element={<Disputes />} />
      <Route path="/console/deposit" element={<ConsoleDeposit />} />
      <Route path="/deposit" element={<ConsoleDeposit />} />
      <Route
        path="/research"
        element={
          <ProtectedRoute>
            <AppShell>
              <Research />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <AppShell>
              <Wallet />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets"
        element={
          <AppShell>
            <Wallets />
          </AppShell>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <AppShell>
            <Leaderboard />
          </AppShell>
        }
      />
      <Route
        path="/pricing"
        element={
          <AppShell>
            <Pricing />
          </AppShell>
        }
      />
      <Route
        path="/burns"
        element={
          <AppShell>
            <Burns />
          </AppShell>
        }
      />
      <Route path="/demo-share" element={<DemoShare />} />
      <Route path="/developers" element={<ApiDocs />} />
      <Route
        path="/submit"
        element={
          <ProtectedRoute>
            <AppShell>
              <Submit />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue"
        element={
          <ProtectedRoute>
            <AppShell>
              <VerificationQueue />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reserve"
        element={
          <AppShell>
            <Reserves />
          </AppShell>
        }
      />
      <Route
        path="/reserves"
        element={
          <AppShell>
            <Reserves />
          </AppShell>
        }
      />
      <Route
        path="/truths"
        element={
          <AppShell>
            <Truths />
          </AppShell>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <AppShell>
              <OperatorWelcome />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sky"
        element={
          <AppShell>
            <Sky />
          </AppShell>
        }
      />
      <Route
        path="/architect/grants"
        element={
          <ProtectedRoute>
            <AppShell>
              <GrantsArchive />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppShell>
              <Settings />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <AppShell>
              <Inbox />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/architect/messages"
        element={
          <ProtectedRoute>
            <AppShell>
              <ArchitectMessages />
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#09090b",
            border: "1px solid #27272a",
            borderRadius: "2px",
            fontFamily: "'JetBrains Mono', monospace",
          },
        }}
      />
    </div>
  );
}

export default App;
